import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Video, BookOpen, ArrowRight, Star, TrendingUp, AlertCircle, ChevronRight, Loader2, History } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { progressService, bookingService, canvasService } from '../api/services';
import useApi from '../hooks/useApi';
import DashboardLayout from '../components/layout/DashboardLayout';
import { ActivityHistory } from '../components/shared/ActivityHistory';
import toast from 'react-hot-toast';

const StatCard = ({ icon: Icon, label, value, color, to }) => {
  const inner = (
    <div className="bg-white border border-cream-200 rounded-2xl p-5 flex flex-col gap-3 hover:shadow-sm transition-shadow">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}><Icon size={18} /></div>
      <div>
        <div className="font-serif text-2xl font-bold text-forest-900">{value ?? '—'}</div>
        <div className="text-xs text-forest-500 mt-0.5">{label}</div>
      </div>
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
};

const DashboardPage = () => {
  const { user } = useAuth();

  const { data: progressData, loading: progressLoading, error: progressError } = useApi(() => progressService.getFull());
  const { data: bookingsData, loading: bookingsLoading } = useApi(
    () => bookingService.getAll({ status: 'CONFIRMED', page: 1, limit: 3 }),
  );
  // History data
  const { data: allBookingsData } = useApi(() => bookingService.getAll({ page: 1, limit: 100 }));
  const { data: canvasData }      = useApi(() => canvasService.getSubmissionsToGrade({ page: 1, limit: 50 }));

  console.log('Progress data:', progressData);

  // ✅ FIX: correct data paths from progressService.getFull() response
  const summary      = progressData?.stats ?? progressData?.summary ?? {};
  const currentLevel = progressData?.currentLevel ?? null;

  // ✅ FIX: get gates from levelProgress for current level, not from currentLevel directly
  const currentLevelProgress = (progressData?.levelProgress ?? []).find(lp =>
    lp.levelId === currentLevel?.id || lp.level?.id === currentLevel?.id
  );

  const upcomingBookings = Array.isArray(bookingsData?.bookings) ? bookingsData.bookings
    : Array.isArray(bookingsData?.data) ? bookingsData.data
    : Array.isArray(bookingsData) ? bookingsData : [];

  const allBookings = Array.isArray(allBookingsData?.bookings) ? allBookingsData.bookings
    : Array.isArray(allBookingsData?.data) ? allBookingsData.data
    : Array.isArray(allBookingsData) ? allBookingsData : [];

  const canvasSubmissions = Array.isArray(canvasData?.submissions) ? canvasData.submissions
    : Array.isArray(canvasData?.data) ? canvasData.data
    : Array.isArray(canvasData) ? canvasData : [];

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  const gates = currentLevel ? [
    {
      label: 'Content Progress', icon: BookOpen,
      done: !!currentLevelProgress?.contentCompleted,
      detail: currentLevelProgress?.contentCompleted ? 'All content watched ✓' : 'Watch all videos and documents',
    },
    {
      label: 'Live Session', icon: Video,
      done: !!currentLevelProgress?.sessionCompleted,
      detail: currentLevelProgress?.sessionCompleted ? 'Session attended ✓' : 'Book and attend an expert session',
    },
    {
      label: 'Canvas Submission ', icon: Star,
      done: !!currentLevelProgress?.canvasCompleted,
      detail: currentLevelProgress?.canvasCompleted ? 'Assignment passed ✓'
        : currentLevelProgress?.sessionCompleted ? 'Ready to submit your assignment'
        : 'Unlocks after live session',
    },
  ] : [];

  const allGatesDone = gates.length > 0 && gates.every(g => g.done);

  const handleAdvance = async () => {
    try {
      await progressService.advance();
      toast.success('Advanced to next level!');
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not advance to next level');
    }
  };

  return (
    <DashboardLayout title={`${greeting}, ${user?.firstName ?? ''} 👋`}>
      <div className="space-y-8 max-w-4xl">

        <p className="text-sm text-forest-500 -mt-4">
          {currentLevel
            ? `${currentLevel.name} · ${allGatesDone ? 'Completed ✓' : 'In Progress'}`
            : progressLoading ? 'Loading your progress...' : 'Welcome to TourEnt Hub'}
        </p>

        {progressError && (
          <div className="flex items-center gap-3 text-rust-500 bg-rust-50 border border-rust-200 rounded-xl px-5 py-4">
            <AlertCircle size={16} className="flex-shrink-0" />
            <span className="text-sm">Could not load progress — make sure your API server is running.</span>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={Video}    label="Sessions Done"  value={progressLoading ? '...' : summary.sessionsDone  ?? 0}              color="text-forest-600 bg-forest-50" to="/bookings" />
          <StatCard icon={Calendar} label="Upcoming"       value={progressLoading ? '...' : summary.upcomingCount ?? upcomingBookings.length} color="text-gold-500 bg-amber-50"    to="/bookings" />
          <StatCard icon={BookOpen} label="Content Done"   value={progressLoading  ? '...' : summary.totalContentCompleted ?? 0}   color="text-blue-600 bg-blue-50"     to="/progress" />
          <StatCard icon={Star}     label="Canvas Graded"  value={progressLoading  ? '...' : summary.canvasSubmitted ?? 0}         color="text-purple-600 bg-purple-50" to="/progress" />
        </div>

        {!progressLoading && currentLevel && (
          <div className="bg-white border border-cream-200 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="font-serif font-semibold text-forest-900 text-lg">{currentLevel.name}</h3>
                <p className="text-xs text-forest-500 mt-0.5">Complete all 3 gates to advance to the next level</p>
              </div>
              <Link to="/progress" className="text-xs text-forest-500 hover:text-forest-800 flex items-center gap-1 font-medium">
                View all <ChevronRight size={13} />
              </Link>
            </div>

            <div className="space-y-3">
              {gates.map((gate) => (
                <div key={gate.label} className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${gate.done ? 'bg-forest-50 border-forest-100' : 'bg-cream-50 border-cream-100'}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold transition-colors ${gate.done ? 'bg-forest-600 text-white' : 'border-2 border-cream-300 text-transparent'}`}>✓</div>
                  <gate.icon size={14} className={gate.done ? 'text-forest-500' : 'text-forest-300'} />
                  <div className="flex-1">
                    <div className={`text-sm font-medium ${gate.done ? 'text-forest-600' : 'text-forest-800'}`}>{gate.label}</div>
                    <div className="text-xs text-forest-400">{gate.detail}</div>
                  </div>
                </div>
              ))}
            </div>

            {allGatesDone && (
              <button onClick={handleAdvance} className="btn-primary w-full justify-center mt-5">
                Advance to Next Level →
              </button>
            )}
          </div>
        )}

        {!bookingsLoading && upcomingBookings.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-serif text-lg text-forest-900">Upcoming Sessions</h3>
              <Link to="/bookings" className="text-xs text-forest-500 hover:text-forest-800 flex items-center gap-1 font-medium">
                View all <ChevronRight size={13} />
              </Link>
            </div>
            <div className="space-y-3">
              {upcomingBookings.map((b) => {
                // ✅ FIX: backend returns expertProfile not expert
                const expertName = `${b.expertProfile?.user?.firstName ?? ''} ${b.expertProfile?.user?.lastName ?? ''}`.trim();
                const date = new Date(b.scheduledAt).toLocaleString('en-GH', { dateStyle: 'medium', timeStyle: 'short' });
                return (
                  <div key={b.id} className="bg-white border border-cream-200 border-l-4 border-l-forest-600 rounded-2xl p-4 flex items-center gap-4">
                    <div className="w-9 h-9 bg-forest-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Video size={16} className="text-forest-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-forest-900">{b.sessionType} with {expertName || 'Expert'}</div>
                      <div className="text-xs text-forest-500 mt-0.5">{date} · {b.duration} mins</div>
                    </div>
                    {b.session?.id ? (
                      <Link to={`/sessions/${b.session.id}`} className="btn-primary text-xs py-1.5 px-4 flex-shrink-0">Join →</Link>
                    ) : (
                      <Link to="/bookings" className="text-xs text-forest-400 flex-shrink-0">Details →</Link>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* <div className="bg-forest-900 rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h3 className="font-serif text-xl text-cream-50">Ready for your next session?</h3>
            <p className="text-sm text-forest-300 mt-1">Browse experts and book your next consultation.</p>
          </div>
          <Link to="/experts" className="btn-rust flex-shrink-0 flex items-center gap-2">
            Find an Expert <ArrowRight size={15} />
          </Link>
        </div> */}

        {/* ── Activity & History ── */}
        <div>
          <div className="flex items-center gap-2 mb-5">
            <History size={20} className="text-forest-600" />
            <h3 className="font-serif text-xl font-semibold text-forest-900">Session History & Activity</h3>
          </div>
          <ActivityHistory
            bookings={allBookings}
            canvasItems={canvasSubmissions}
            role="CLIENT"
          />
        </div>

      </div>
    </DashboardLayout>
  );
};

export default DashboardPage;